import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MobileShell } from "@/components/MobileShell";
import { BrandHeader } from "@/components/BrandHeader";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { Bluetooth, Check, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { formatMoney } from "@/lib/money";
import { simAdvertise, simDrainInbox } from "@/lib/ble/transport";
import { getOrCreateKeypair, signVoucher } from "@/lib/ble/keys";
import { enqueueVoucher, syncOne } from "@/lib/ble/queue";
import { getDeviceId } from "@/lib/device";

interface Incoming {
  id: string;
  sender_id: string;
  amount_cents: number;
  currency: string;
  note?: string | null;
  expires_at: string;
  raw: any;
}

const ReceiveNearby = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [incoming, setIncoming] = useState<Incoming[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const [profile, setProfile] = useState<{ display_name?: string | null; cashtag?: string | null } | null>(null);

  useEffect(() => {
    if (!user) return;
    supabase
      .from("profiles")
      .select("display_name,cashtag")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data }) => setProfile(data as any));
  }, [user]);

  // Advertise (web sim) + drain inbox
  useEffect(() => {
    if (!user) return;
    void getOrCreateKeypair();
    simAdvertise({
      device_id: getDeviceId(),
      user_id: user.id,
      display_name: profile?.display_name || "Cash Isle user",
      cashtag: profile?.cashtag,
    });
    const poll = () => {
      const items = simDrainInbox(user.id);
      if (items.length) {
        setIncoming((prev) => {
          const seen = new Set(prev.map((p) => p.id));
          const next = [...prev];
          for (const json of items) {
            try {
              const v = JSON.parse(json);
              if (!seen.has(v.id)) {
                next.push({
                  id: v.id,
                  sender_id: v.sender_id,
                  amount_cents: v.amount_cents,
                  currency: v.currency,
                  note: v.note,
                  expires_at: v.expires_at,
                  raw: v,
                });
              }
            } catch { /* ignore */ }
          }
          return next;
        });
      }
    };
    poll();
    const t = setInterval(poll, 1500);
    return () => clearInterval(t);
  }, [user, profile]);

  const accept = async (item: Incoming) => {
    if (!user) return;
    setBusy(item.id);
    try {
      const { sk } = await getOrCreateKeypair();
      const ack = await signVoucher(sk, {
        id: item.raw.id,
        sender_id: item.raw.sender_id,
        receiver_id: item.raw.receiver_id,
        amount_cents: item.raw.amount_cents,
        currency: item.raw.currency,
        nonce: item.raw.nonce,
        expires_at: item.raw.expires_at,
      });

      const queued = {
        id: item.id,
        role: "receiver" as const,
        payload: item.raw,
        receiver_ack_signature: ack,
        receiver_device_id: getDeviceId(),
        created_at: Date.now(),
        status: "queued" as const,
      };
      await enqueueVoucher(queued);

      // Try immediate settle
      const r = await syncOne(queued);
      if (r.ok) {
        toast.success(`Received ${formatMoney(item.amount_cents)} 🌴`);
      } else {
        toast.message("Saved — will settle when online");
      }
      setIncoming((p) => p.filter((x) => x.id !== item.id));
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setBusy(null);
    }
  };

  const reject = async (item: Incoming) => {
    setBusy(item.id);
    try {
      await supabase.functions.invoke("ble-voucher-reject", {
        body: { voucher_id: item.id, reason: "receiver_declined" },
      });
      setIncoming((p) => p.filter((x) => x.id !== item.id));
      toast.message("Declined");
    } finally {
      setBusy(null);
    }
  };

  return (
    <MobileShell>
      <BrandHeader onBack={() => navigate(-1)}>
        <h1 className="text-center mt-5 font-display text-2xl font-bold flex items-center justify-center gap-2">
          <Bluetooth className="h-6 w-6" /> Receive Nearby
        </h1>
      </BrandHeader>

      <div className="px-5 py-6 flex-1 space-y-5">
        <div className="rounded-2xl bg-gradient-hero text-primary-foreground p-6 text-center">
          <Bluetooth className="h-10 w-10 mx-auto animate-pulse" />
          <div className="mt-3 font-display text-lg font-semibold">Visible to nearby senders</div>
          <div className="text-sm opacity-90">
            {profile?.cashtag ? `$${profile.cashtag}` : "Keep this screen open"}
          </div>
        </div>

        {incoming.length === 0 && (
          <div className="rounded-xl border border-dashed p-6 text-center text-sm text-muted-foreground">
            Waiting for incoming transfers…
          </div>
        )}

        {incoming.map((item) => (
          <div key={item.id} className="rounded-xl border bg-card p-4">
            <div className="flex items-baseline justify-between">
              <div className="font-display text-2xl font-bold">
                {formatMoney(item.amount_cents, item.currency)}
              </div>
              <div className="text-xs text-muted-foreground">
                Expires {new Date(item.expires_at).toLocaleString()}
              </div>
            </div>
            {item.note && <div className="text-sm mt-2">"{item.note}"</div>}
            <div className="text-xs text-muted-foreground mt-1">From: {item.sender_id.slice(0, 8)}…</div>
            <div className="mt-3 grid grid-cols-2 gap-2">
              <Button variant="outline" onClick={() => reject(item)} disabled={busy === item.id}>
                <X className="h-4 w-4" /> Decline
              </Button>
              <Button variant="hero" onClick={() => accept(item)} disabled={busy === item.id}>
                {busy === item.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                Accept
              </Button>
            </div>
          </div>
        ))}
      </div>
      <div className="safe-bottom" />
    </MobileShell>
  );
};

export default ReceiveNearby;
